/**
 * CRM Settings — Currency Settings
 *
 * Tenant-wide currency configuration: primary currency, rate source
 * (manual vs API provider), refresh schedule, and manual "Fetch now".
 * Provider API key is password-masked and disabled for ECB (which is
 * keyless) and for the manual rate source.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Loader2, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/crm/currency-settings')({
  component: CurrencySettingsPage,
})

interface Settings {
  id: string
  primaryCurrency: string
  rateSource: string
  providerKey: string | null
  refreshSchedule: string
  lastRefreshAt: Date | null
  lastRefreshStatus: string | null
  lastRefreshError: string | null
}

type RateSource = 'manual' | 'api'
type Provider = 'ecb' | 'exchangerates_api' | 'openexchangerates' | 'fixer'
type Schedule = 'daily' | 'weekly' | 'on_demand'

function CurrencySettingsPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const settingsQ = useQuery(orpc.crm.currency.settings.get.queryOptions({ input: {} }))
  const settings = settingsQ.data as Settings | undefined

  const [primaryCurrency, setPrimaryCurrency] = useState('USD')
  const [rateSource, setRateSource] = useState<RateSource>('manual')
  const [providerKey, setProviderKey] = useState<Provider>('ecb')
  const [providerApiKey, setProviderApiKey] = useState('')
  const [refreshSchedule, setRefreshSchedule] = useState<Schedule>('on_demand')

  useEffect(() => {
    if (settings) {
      setPrimaryCurrency(settings.primaryCurrency)
      setRateSource((settings.rateSource as RateSource) ?? 'manual')
      setProviderKey((settings.providerKey as Provider) ?? 'ecb')
      setRefreshSchedule((settings.refreshSchedule as Schedule) ?? 'on_demand')
    }
  }, [settings])

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: orpc.crm.currency.settings.get.queryKey() })

  const updateMut = useMutation(
    orpc.crm.currency.settings.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.save'))
        setProviderApiKey('')
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const fetchNowMut = useMutation(
    orpc.crm.currency.rates.fetchNow.mutationOptions({
      onSuccess: (data: unknown) => {
        const d = data as { fetched: number }
        toast.success(`${d.fetched} ${t('crm.settings.currency.rates').toLowerCase()}`)
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const providerKeyDisabled = rateSource === 'manual' || providerKey === 'ecb'

  if (settingsQ.isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (settingsQ.isError) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('crm.common.noRecords')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('crm.settings.currency.title')}</h1>
        <p className="text-muted-foreground">{t('navigation.crmSettings')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('navigation.general')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="currency">{t('crm.settings.currency.primaryCurrency')}</Label>
            <Input
              id="currency"
              value={primaryCurrency}
              maxLength={3}
              onChange={(e) => setPrimaryCurrency(e.target.value.toUpperCase())}
              className="max-w-[100px] uppercase"
              placeholder="USD"
            />
          </div>

          <div>
            <Label>{t('crm.settings.currency.rateSource')}</Label>
            <RadioGroup
              value={rateSource}
              onValueChange={(v) => setRateSource(v as RateSource)}
              className="mt-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="manual" id="src-manual" />
                <Label htmlFor="src-manual" className="font-normal">
                  {t('crm.settings.currency.rateSourceManual')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="api" id="src-api" />
                <Label htmlFor="src-api" className="font-normal">
                  {t('crm.settings.currency.rateSourceApi')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="provider">{t('crm.settings.currency.provider')}</Label>
            <Select
              value={providerKey}
              onValueChange={(v) => setProviderKey(v as Provider)}
              disabled={rateSource !== 'api'}
            >
              <SelectTrigger id="provider" className="max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ecb">European Central Bank (keyless)</SelectItem>
                <SelectItem value="exchangerates_api">exchangerates.io</SelectItem>
                <SelectItem value="openexchangerates">Open Exchange Rates</SelectItem>
                <SelectItem value="fixer">Fixer.io</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="api-key">{t('crm.settings.currency.providerKey')}</Label>
            <Input
              id="api-key"
              type="password"
              value={providerApiKey}
              onChange={(e) => setProviderApiKey(e.target.value)}
              disabled={providerKeyDisabled}
              placeholder={
                providerKeyDisabled ? t('crm.common.optional') : t('crm.common.optional')
              }
              className="max-w-md"
            />
            <p className="text-xs text-muted-foreground mt-1">
              ECB is keyless. Leave blank to keep the existing value.
            </p>
          </div>

          <div>
            <Label>{t('crm.settings.currency.refreshSchedule')}</Label>
            <RadioGroup
              value={refreshSchedule}
              onValueChange={(v) => setRefreshSchedule(v as Schedule)}
              className="mt-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="daily" id="sch-daily" />
                <Label htmlFor="sch-daily" className="font-normal">
                  {t('crm.settings.currency.scheduleDaily')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="weekly" id="sch-weekly" />
                <Label htmlFor="sch-weekly" className="font-normal">
                  {t('crm.settings.currency.scheduleWeekly')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="on_demand" id="sch-ondemand" />
                <Label htmlFor="sch-ondemand" className="font-normal">
                  {t('crm.settings.currency.scheduleOnDemand')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={() =>
                updateMut.mutate({
                  primaryCurrency,
                  rateSource,
                  providerKey,
                  refreshSchedule,
                  ...(providerApiKey ? { providerApiKey } : {}),
                })
              }
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('crm.common.save')
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => fetchNowMut.mutate({})}
              disabled={rateSource !== 'api' || fetchNowMut.isPending}
            >
              {fetchNowMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t('crm.settings.currency.fetchNow')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {settings && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('crm.settings.currency.lastRefresh')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-32">{t('crm.common.updatedAt')}</span>
              <span>
                {settings.lastRefreshAt ? new Date(settings.lastRefreshAt).toLocaleString() : '-'}
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-muted-foreground w-32">{t('crm.common.status')}</span>
              {settings.lastRefreshStatus ? (
                <Badge
                  variant={settings.lastRefreshStatus === 'success' ? 'default' : 'destructive'}
                  className={settings.lastRefreshStatus === 'success' ? 'bg-green-500' : ''}
                >
                  {settings.lastRefreshStatus}
                </Badge>
              ) : (
                <span>-</span>
              )}
            </div>
            {settings.lastRefreshError && (
              <div className="flex gap-2">
                <span className="text-muted-foreground w-32">{t('crm.common.status')}</span>
                <span className="text-red-600">{settings.lastRefreshError}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
